import { useState, useCallback, useEffect } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
// TODO: Re-enable Menu icon when Central tab is restored
import { Zap, X, /* Menu, */ Bell, HelpCircle, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// TODO: Re-enable when backend is ready
// import HomeTab from './tabs/HomeTab';
import NotificationsTab from './tabs/NotificationsTab';
import SupportTab from './tabs/SupportTab';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import { useUserId } from 'hooks/useCurrentUser';
import { useNotificationPreferences } from 'hooks/useNotificationPreferences';
import { useNotifications } from 'hooks/useNotifications';
import { cn } from 'lib/utils';
import { useIsMobile } from 'utils/mediaQueries';

// TODO: Re-enable 'home' when backend is ready
type TabType = /* 'home' | */ 'notifications' | 'support';

const FloatingWidget = () => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('notifications');
  const isMobile = useIsMobile();
  const { unreadCount } = useNotifications();
  const userId = useUserId();
  const { isDNDActive } = useNotificationPreferences(userId ?? undefined);

  // Toggle widget open/close
  const toggleWidget = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Close widget on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Prevent body scroll when widget is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isMobile, isOpen]);

  return (
    <>
      {/* FAB - Floating Action Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-[9999]"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        <div className="relative">
          <Button
            onClick={toggleWidget}
            size="icon"
            className={cn(
              'h-14 w-14 rounded-full shadow-lg',
              'hover:scale-110 transition-all duration-200',
              'bg-primary hover:bg-primary/90',
              // Focus ring sem offset para evitar contorno branco
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
            )}
            aria-label={isOpen ? t('widget.closeLabel') : t('widget.openLabel')}
            aria-expanded={isOpen}
          >
            <motion.div
              initial={false}
              animate={{ rotate: isOpen ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Zap className="h-6 w-6" />}
            </motion.div>
          </Button>
          {/* Notification Badge */}
          {!isOpen && unreadCount > 0 && (
            <Badge
              variant="destructive"
              className={cn(
                'absolute -top-1 -right-1 h-5 min-w-[20px] px-1.5',
                'flex items-center justify-center',
                'text-xs font-bold',
                'animate-pulse'
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
          {/* DND Indicator */}
          {!isOpen && isDNDActive && (
            <div
              className="absolute -top-1 -left-1 h-5 w-5 rounded-full bg-warning flex items-center justify-center"
              title={t('widget.dndActive')}
            >
              <Moon className="h-3 w-3 text-warning-foreground" />
            </div>
          )}
        </div>
      </motion.div>

      {/* Expanded Widget Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop for mobile */}
            {isMobile && (
              <motion.div
                className="fixed inset-0 bg-black/50 z-[9997]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={toggleWidget}
                aria-hidden="true"
              />
            )}

            {/* Widget Panel */}
            <motion.div
              className={cn(
                'fixed z-[9998] isolate',
                isMobile ? 'inset-0 m-0' : 'bottom-24 right-6 w-[380px] h-[550px]'
              )}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="widget-title"
            >
              <Card
                className={cn(
                  'h-full flex flex-col shadow-2xl overflow-hidden',
                  'border border-border/50',
                  isMobile ? 'rounded-none' : 'rounded-2xl'
                )}
              >
                <CardHeader className="pb-3 border-b border-border/30 bg-background">
                  <div className="flex items-center justify-between">
                    <h2 id="widget-title" className="text-lg font-semibold">
                      {t('widget.title')}
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleWidget}
                      className={cn(
                        'h-8 w-8 rounded-full',
                        'hover:bg-muted transition-all duration-200',
                        'hover:scale-110 active:scale-95',
                        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50'
                      )}
                      aria-label={t('widget.close')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 overflow-hidden p-0">
                  <Tabs
                    value={activeTab}
                    onValueChange={value => setActiveTab(value as TabType)}
                    className="h-full flex flex-col"
                  >
                    {/* Tab Content Area */}
                    <div className="flex-1 overflow-y-auto p-4">
                      {/* TODO: Re-enable HomeTab when backend is ready */}
                      {/* <TabsContent value="home" className="mt-0 h-full">
                        <HomeTab onTabChange={tab => setActiveTab(tab as TabType)} />
                      </TabsContent> */}

                      <TabsContent value="notifications" className="mt-0 h-full">
                        <NotificationsTab />
                      </TabsContent>

                      <TabsContent value="support" className="mt-0 h-full">
                        <SupportTab />
                      </TabsContent>
                    </div>

                    {/* Tab Navigation - Fixed at Bottom */}
                    <div className="border-t border-border/30 bg-background">
                      <TabsList className="w-full h-16 grid grid-cols-2 rounded-none bg-transparent p-0">
                        {/* TODO: Re-enable Central tab when backend is ready */}
                        {/* <TabsTrigger
                          value="home"
                          className={cn(
                            'flex-col gap-1 h-full rounded-none',
                            'border-t-2 border-transparent',
                            'data-[state=active]:border-primary data-[state=active]:bg-primary/10',
                            'data-[state=active]:text-primary',
                            'transition-all duration-200'
                          )}
                          aria-label="Página inicial"
                        >
                          <Menu className="h-5 w-5" />
                          <span className="text-xs">Central</span>
                        </TabsTrigger> */}
                        <TabsTrigger
                          value="notifications"
                          className={cn(
                            'flex-col gap-1 h-full rounded-none relative',
                            'border-t-2 border-transparent',
                            'data-[state=active]:border-primary data-[state=active]:bg-primary/10',
                            'data-[state=active]:text-primary',
                            'transition-all duration-200'
                          )}
                          aria-label={t('widget.tabNotifications')}
                        >
                          <div className="relative">
                            <Bell className="h-5 w-5" />
                            {unreadCount > 0 && (
                              <span className="absolute -top-1 -right-1 w-2 h-2 bg-destructive rounded-full" />
                            )}
                          </div>
                          <span className="text-xs">{t('widget.tabNotifications')}</span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="support"
                          className={cn(
                            'flex-col gap-1 h-full rounded-none',
                            'border-t-2 border-transparent',
                            'data-[state=active]:border-primary data-[state=active]:bg-primary/10',
                            'data-[state=active]:text-primary',
                            'transition-all duration-200'
                          )}
                          aria-label={t('widget.tabSupport')}
                        >
                          <HelpCircle className="h-5 w-5" />
                          <span className="text-xs">{t('widget.tabSupport')}</span>
                        </TabsTrigger>
                      </TabsList>
                    </div>
                  </Tabs>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingWidget;
